import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import { useSiteContent } from "@/pages/Conteudos";
import { Loader2 } from "lucide-react";

export default function LegalPage({ page }) {
  const content = useSiteContent();
  const [doc, setDoc] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setDoc(null);
    setNotFound(false);
    api.get(`/public/legal/${page}`)
      .then(({ data }) => setDoc(data))
      .catch(() => setNotFound(true));
  }, [page]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
        <Navbar content={content} />
        <main className="flex-1 grid place-items-center px-5">
          <div className="text-center">
            <h1 className="font-display text-4xl text-[#2C332A]">Página não encontrada</h1>
            <Link to="/" className="inline-flex items-center rounded-full bg-[#5B6E5B] px-6 py-3 mt-6 text-[#FDFBF7] font-body text-sm hover:bg-[#4A5D4E] transition-colors">
              Voltar ao início
            </Link>
          </div>
        </main>
        <Footer content={content} />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FDFBF7]">
        <Loader2 className="w-7 h-7 animate-spin text-[#5B6E5B]" data-testid="legal-loading" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col" data-testid={`${page}-page`}>
      <Helmet>
        <title>{doc.title}</title>
        <meta name="description" content={doc.description} />
        <link rel="canonical" href={`${window.location.origin}/${page}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={doc.title} />
        <meta property="og:description" content={doc.description} />
        <meta property="og:url" content={`${window.location.origin}/${page}`} />
      </Helmet>
      <Navbar content={content} />
      <main className="flex-1 max-w-3xl mx-auto w-full px-5 pt-32 pb-20">
        <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Institucional</p>
        <h1 className="font-display text-4xl sm:text-5xl text-[#2C332A] mt-3 leading-tight" data-testid="legal-title">{doc.heading}</h1>
        {(doc.updated_at || (page === "politica-de-privacidade" && doc.legal_email)) && (
          <p className="font-body text-xs text-[#7A8A7A] mt-3" data-testid="legal-updated">
            {doc.updated_at && <>Última atualização: {doc.updated_at}</>}
            {page === "politica-de-privacidade" && doc.legal_email && <> · Dúvidas sobre dados pessoais: {doc.legal_email}</>}
          </p>
        )}
        <article
          className="mt-8 font-body text-[#2C332A] leading-relaxed
            [&_h2]:font-display [&_h2]:text-3xl [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:leading-snug
            [&_h3]:font-display [&_h3]:text-2xl [&_h3]:mt-8 [&_h3]:mb-2
            [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:pl-6 [&_ul]:list-disc [&_ol]:mb-4 [&_ol]:pl-6 [&_ol]:list-decimal
            [&_li]:mb-1.5 [&_strong]:text-[#4A5D4E] [&_a]:text-[#5B6E5B] [&_a]:underline
            [&_blockquote]:border-l-[3px] [&_blockquote]:border-[#C87A5E] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[#4A5D4E] [&_blockquote]:mb-4"
          data-testid="legal-body"
          dangerouslySetInnerHTML={{ __html: doc.body_html }}
        />
      </main>
      <Footer content={content} />
      <WhatsAppFloat content={content} />
    </div>
  );
}
